import { data, Link, useLoaderData } from 'react-router'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import { ScoreMark } from '#app/components/score.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { getPublishablePages } from '#app/scoring/publishable-pages.server.ts'
import {
	type TieBreakDemo,
	getTieBreakDemo,
} from '#app/scoring/tie-break.server.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'
import { getMarketingNav } from './nav.server.ts'

export async function loader({}: Route.LoaderArgs) {
	const [categories, cities, homeSeo] = await Promise.all([
		prisma.category.findMany({
			where: {
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				pluralName: true,
				slug: true,
				_count: {
					select: {
						businesses: {
							where: { status: 'published' },
						},
					},
				},
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.city.findMany({
			where: {
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
				_count: {
					select: {
						businesses: {
							where: { status: 'published' },
						},
					},
				},
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.sEOSettings.findUnique({
			where: { page: 'HOME' },
			select: {
				title: true,
				description: true,
				canonicalUrl: true,
				focusKeyword: true,
				seoKeywords: true,
				noIndex: true,
				ogTitle: true,
				ogDescription: true,
				ogImage: true,
				twitterTitle: true,
				twitterDescription: true,
				structuredData: true,
			},
		}),
	])

	// Get total published business count for social proof
	const totalBusinesses = await prisma.business.count({
		where: { status: 'published' },
	})

	/*
	 * The homepage is the single strongest source of internal links on the
	 * site, so none of them should land on a redirect. A category whose ranking
	 * publishes now 301s from its hub to /best/{slug}; link the destination.
	 */
	const rankingSlugs = new Set(
		(await getPublishablePages()).bestCategories.map((c) => c.slug),
	)

	/*
	 * The opening demonstration.
	 *
	 * Returns null until a category has enough scored businesses sharing one
	 * rating to make the point, and the hero falls back to a plain sentence —
	 * so the page is correct on a catalog that has not been scored yet without
	 * showing an empty frame where the argument should be.
	 */
	const [tieBreak, popularPairs] = await Promise.all([
		getTieBreakDemo(),
		getMarketingNav().then((nav) => nav.popularPairs),
	])

	return data({
		categories: categories.map((category) => ({
			...category,
			ranked: rankingSlugs.has(category.slug),
			href: rankingSlugs.has(category.slug)
				? `/best/${category.slug}`
				: `/categories/${category.slug}`,
		})),
		cities,
		totalBusinesses,
		homeSeo,
		tieBreak,
		popularPairs,
		rankedCount: rankingSlugs.size,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'home' } satisfies RouteAnalyticsHandle,
	breadcrumb() {
		return { name: 'Home', url: '/' }
	},
	seo({
		loaderData,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		canonical: string
	}): SeoProps {
		const seo = loaderData.homeSeo

		const keywords = [seo?.focusKeyword, ...(seo?.seoKeywords || [])].filter(
			Boolean,
		)

		/*
		 * Deliberately names no vertical. The catalog spans roofing, plumbing,
		 * dentistry, accounting, legal and ~25 other categories, and an earlier
		 * title that said "Home Services" described a fraction of it.
		 */
		const defaultTitle =
			'DFW Professionals | Dallas–Fort Worth businesses, scored on their reviews'
		const defaultDescription = `We track ${loaderData.totalBusinesses.toLocaleString()} businesses across ${loaderData.categories.length} categories in Dallas–Fort Worth and rank them on what reviewers wrote, not the star they clicked. Published rubric, cited claims, no paid placement.`

		const structuredData: Array<object | string> = []

		/*
		 * No featured ItemList. This page queried businesses flagged isFeatured
		 * and published them to Google and to models as an ItemList, while
		 * rendering none of them anywhere on the page.
		 *
		 * Two problems. Structured data must describe content the reader can
		 * actually see, and this described nothing. And it promoted a hand-picked
		 * set to exactly the machines whose citations this site is built to earn,
		 * with no indication the selection was not earned.
		 */
		if (seo?.structuredData) {
			const customData = seo.structuredData
			if (Array.isArray(customData)) {
				structuredData.push(...(customData as Array<object | string>))
			} else if (customData !== null) {
				structuredData.push(customData as object | string)
			}
		}

		return {
			title: seo?.title || defaultTitle,
			description: seo?.description || defaultDescription,
			canonical: seo?.canonicalUrl || canonical,
			keywords: keywords.length > 0 ? keywords : undefined,
			robots: seo?.noIndex ? 'noindex, nofollow' : undefined,
			openGraph: {
				title: seo?.ogTitle || defaultTitle,
				description: seo?.ogDescription || defaultDescription,
				...(seo?.ogImage && { image: seo.ogImage }),
				type: 'website',
				url: seo?.canonicalUrl || canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: seo?.twitterTitle || defaultTitle,
				description: seo?.twitterDescription || defaultDescription,
			},
			structuredData: structuredData.length > 0 ? structuredData : undefined,
		}
	},
}

/**
 * Homepage.
 *
 * The job of this page is to establish, in one screen, that this site knows
 * something a star rating does not — and then get the reader into a category
 * or a city fast.
 *
 * It opens by reproducing the reader's own experience. Searching for a local
 * business returns a wall of businesses all showing the same rating, and no
 * way to choose between them. The page shows that wall using real listings,
 * then breaks it with real scores and a real caution.
 *
 * Everything is derived. The demonstration picks whichever category currently
 * shows the widest score spread within a single shared rating, so it follows
 * the data rather than a vertical — today that is dentists, and it was roofers
 * a week earlier.
 */

/**
 * The tie, and the break.
 *
 * Two encodings doing opposite jobs. The rating is identical on every row —
 * that flatness IS the argument — while the score range spreads across a
 * shared 0–100 track, so the eye compares trades directly.
 *
 * Widths, not heights. A percentage height needs a parent with a resolved
 * height, which is what silently collapsed the figure this replaced; a block
 * element's width resolves against its container with no such dependency.
 */
function SpreadTrack({ from, to }: { from: number; to: number }) {
	return (
		<div
			className="bg-muted-foreground/15 relative h-1 w-full rounded-full"
			aria-hidden
		>
			<div
				className="bg-accent absolute inset-y-0 rounded-full"
				style={{ left: `${from}%`, width: `${Math.max(1.5, to - from)}%` }}
			/>
		</div>
	)
}

/**
 * The tie, and the break.
 *
 * Two encodings doing opposite jobs: the rating repeats on every row — that
 * flatness IS the argument — while the score range spreads across a shared
 * 0–100 track, so trades can be compared to each other directly.
 *
 * Deliberately borderless. An earlier version ruled every row of both lists
 * and then put a top border on each audience column, which stacked three
 * bordered blocks and read as boxes inside boxes. The bars already supply the
 * horizontal rhythm a table would need rules for, and the score column
 * supplies a vertical axis, so the lines were decorating structure that was
 * already there. Separation comes from space and type weight; the only
 * interactive rows carry a hover wash instead, which is feedback rather than
 * ornament.
 *
 * Widths, not heights. A percentage height needs a parent with a resolved
 * height, which is what silently collapsed the figure this replaced.
 */
function TieBreak({ demo }: { demo: TieBreakDemo }) {
	const { featured } = demo

	return (
		<div className="mt-14">
			<ul className="space-y-1">
				{demo.categories.map((category) => (
					<li key={category.categorySlug}>
						<Link
							to={category.href}
							className="group hover:bg-muted/50 -mx-3 grid grid-cols-[1fr_auto] items-center gap-x-5 gap-y-2 rounded-md px-3 py-2.5 transition-colors sm:grid-cols-[13rem_5.5rem_1fr_3.5rem]"
						>
							<span className="text-sm font-medium group-hover:underline">
								{categoryPlural({
									name: category.categoryName,
									pluralName: category.categoryPluralName,
								})}
							</span>
							<span className="text-muted-foreground text-right font-mono text-xs tabular-nums sm:text-left">
								{category.tiedCount} at {category.ratingValue.toFixed(1)}★
							</span>
							<span className="col-span-2 sm:col-span-1">
								<SpreadTrack
									from={category.lowestScore}
									to={category.highestScore}
								/>
							</span>
							<span className="text-accent col-span-2 font-mono text-xs tabular-nums sm:col-span-1 sm:text-right">
								{category.lowestScore.toFixed(0)}–
								{category.highestScore.toFixed(0)}
							</span>
						</Link>
					</li>
				))}
			</ul>

			{/* One trade made concrete, so the pattern is not left as an abstraction. */}
			<figure className="mt-12">
				<figcaption className="text-muted-foreground mb-5 font-mono text-xs tracking-widest uppercase">
					{categoryPlural({
						name: featured.categoryName,
						pluralName: featured.categoryPluralName,
					})}{' '}
					at {featured.ratingValue.toFixed(1)}★
				</figcaption>
				<ul className="space-y-5">
					{featured.examples.map((example) => (
						<li
							key={example.slug}
							className="flex items-baseline justify-between gap-6"
						>
							<div className="min-w-0">
								<Link
									className="font-medium hover:underline"
									to={`/business/${example.slug}`}
								>
									{example.name}
								</Link>
								{example.watchOut ? (
									<p className="text-muted-foreground mt-1 line-clamp-1 text-xs">
										{example.watchOut}
									</p>
								) : null}
							</div>
							<ScoreMark
								score={example.score}
								size="plain"
								className="shrink-0 text-lg leading-none"
							/>
						</li>
					))}
				</ul>
			</figure>
		</div>
	)
}

export default function EditorialHome() {
	const {
		categories,
		cities,
		totalBusinesses,
		tieBreak,
		popularPairs,
		rankedCount,
	} = useLoaderData<typeof loader>()

	/*
	 * Both columns show the same number of rows so neither axis reads as the
	 * secondary one, and both are ordered by how much of the catalog they
	 * actually cover rather than alphabetically — an A-to-Z list buries Dallas
	 * and Fort Worth behind Allen and Arlington.
	 */
	const BROWSE_ROWS = 10
	const topCategories = [...categories]
		.sort((a, b) => b._count.businesses - a._count.businesses)
		.slice(0, BROWSE_ROWS)
	const topCities = [...cities]
		.sort((a, b) => b._count.businesses - a._count.businesses)
		.slice(0, BROWSE_ROWS)

	return (
		<>
			{/* ── The argument, shown ─────────────────────────────────────────── */}
			<section className="container mx-auto px-4 pt-16 pb-20 md:pt-24">
				<div className="mx-auto max-w-5xl">
					{/*
					 * The metro's footprint, stated before anything else. This is a
					 * Dallas–Fort Worth directory and place is half its identity, so
					 * the coverage numbers open the page rather than sitting in a
					 * footer as afterthought social proof.
					 */}
					<p className="text-muted-foreground mb-5 font-mono text-xs tracking-widest tabular-nums">
						{cities.length} DFW {cities.length === 1 ? 'CITY' : 'CITIES'} ·{' '}
						{categories.length}{' '}
						{categories.length === 1 ? 'CATEGORY' : 'CATEGORIES'} ·{' '}
						{totalBusinesses.toLocaleString()} BUSINESSES
					</p>

					{/*
					 * The headline names the metro rather than saying "here". A local
					 * directory that could be about anywhere is not a local directory,
					 * and "Dallas–Fort Worth" is also the phrase a model needs in the
					 * sentence it quotes.
					 */}
					<h1 className="mb-6 max-w-3xl font-serif text-4xl leading-[1.05] tracking-tight text-balance md:text-6xl">
						In Dallas–Fort Worth, everyone has five stars.
						<br />
						<span className="text-muted-foreground">
							We read what people actually wrote.
						</span>
					</h1>

					<p className="text-muted-foreground max-w-xl text-lg leading-relaxed">
						{tieBreak
							? `${tieBreak.totalTied.toLocaleString()} Dallas–Fort Worth businesses across ${tieBreak.categories.length} trades hold a perfect ${tieBreak.categories[0]!.ratingValue.toFixed(1)}. The star stopped separating them years ago, so we score the text of their reviews instead.`
							: `We track ${totalBusinesses.toLocaleString()} businesses across Dallas–Fort Worth and rank them on the text of their reviews, not the star their customers clicked.`}
					</p>

					{tieBreak ? <TieBreak demo={tieBreak} /> : null}

					{/*
					 * Two audiences, named explicitly rather than blended into one
					 * sentence that serves neither. Someone hiring wants the list; an
					 * owner who just saw their trade ranked wants to know where they
					 * landed. The second is the top of the revenue funnel, so it gets
					 * equal billing without being dressed up as editorial.
					 *
					 * One rule above the pair, not one per column. Two bordered
					 * columns read as two boxes; a single line reads as the hero
					 * ending and the paths beginning, which is what it is.
					 */}
					<div className="mt-14 grid gap-x-12 gap-y-8 border-t pt-8 sm:grid-cols-2">
						<div>
							<h2 className="mb-1.5 font-medium">Hiring someone?</h2>
							<p className="text-muted-foreground mb-3 text-sm leading-relaxed">
								Every ranking shows what reviewers repeatedly praise and what
								they repeatedly warn about, with the reviews attached.
							</p>
							<Link
								className="text-sm font-medium underline underline-offset-4"
								to="/categories"
							>
								Browse {categories.length} categories
							</Link>
						</div>
						<div>
							<h2 className="mb-1.5 font-medium">
								Run one of these businesses?
							</h2>
							<p className="text-muted-foreground mb-3 text-sm leading-relaxed">
								You already have the reviews. See the score they produce, the
								recurring complaints pulling it down, and where you sit against
								your street.
							</p>
							<Link
								className="text-sm font-medium underline underline-offset-4"
								to="/search"
							>
								Find your listing
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* ── Browse: trade and place, presented as equals ────────────────── */}
			<section className="bg-muted/30 border-y py-16">
				<div className="container mx-auto px-4">
					<div className="mx-auto max-w-5xl">
						<h2 className="mb-2 font-serif text-2xl tracking-tight">
							Start with a trade, or with where you live
						</h2>
						<p className="text-muted-foreground mb-10 max-w-xl leading-relaxed">
							Most searches name both: a trade and a city. Either column gets
							you there.
						</p>

						{/*
						 * Two axes side by side rather than a trade grid with cities
						 * appended near the footer. On a metro directory place is not a
						 * secondary filter, it is half of what the reader is choosing,
						 * and the demand data agrees: city-qualified service terms carry
						 * roughly ten times the volume of metro-wide ones.
						 */}
						<div className="grid gap-12 md:grid-cols-2">
							<div>
								<div className="mb-4 flex items-baseline justify-between gap-3 border-b pb-2">
									<h3 className="font-mono text-xs tracking-widest uppercase">
										By trade
									</h3>
									<Link
										className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4"
										to="/categories"
									>
										All {categories.length}
									</Link>
								</div>
								<ul>
									{topCategories.map((category) => (
										<li key={category.id} className="border-b last:border-0">
											<Link
												to={category.href}
												className="group flex items-baseline justify-between gap-3 py-2.5"
											>
												<span className="text-sm font-medium group-hover:underline">
													{category.ranked
														? `Best ${categoryPlural(category)}`
														: categoryPlural(category)}
												</span>
												<span className="text-muted-foreground font-mono text-xs tabular-nums">
													{category._count.businesses}
												</span>
											</Link>
										</li>
									))}
								</ul>
							</div>

							<div>
								<div className="mb-4 flex items-baseline justify-between gap-3 border-b pb-2">
									<h3 className="font-mono text-xs tracking-widest uppercase">
										By city
									</h3>
									<Link
										className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4"
										to="/cities"
									>
										All {cities.length}
									</Link>
								</div>
								<ul>
									{topCities.map((city) => (
										<li key={city.id} className="border-b last:border-0">
											<Link
												to={`/cities/${city.slug}`}
												className="group flex items-baseline justify-between gap-3 py-2.5"
											>
												<span className="text-sm font-medium group-hover:underline">
													{city.name}
												</span>
												<span className="text-muted-foreground font-mono text-xs tabular-nums">
													{city._count.businesses}
												</span>
											</Link>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ── Where demand actually is ────────────────────────────────────── */}
			{popularPairs.length > 0 ? (
				<section className="py-16">
					<div className="container mx-auto px-4">
						<div className="mx-auto max-w-5xl">
							<h2 className="mb-2 font-serif text-2xl tracking-tight">
								Ranked in your city
							</h2>
							<p className="text-muted-foreground mb-8 max-w-xl leading-relaxed">
								Most people search for a trade and a city together. These are
								the local rankings with enough reviewed businesses to publish.
							</p>
							<ul className="flex flex-wrap gap-2">
								{popularPairs.map((pair) => (
									<li key={`${pair.citySlug}/${pair.categorySlug}`}>
										<Link
											className="hover:border-foreground inline-block rounded-full border px-4 py-2 text-sm transition-colors"
											to={`/${pair.citySlug}/${pair.categorySlug}`}
										>
											{categoryPlural({
												name: pair.categoryName,
												pluralName: pair.categoryPluralName,
											})}{' '}
											in {pair.cityName}
											<span className="text-muted-foreground ml-2 font-mono text-xs tabular-nums">
												{pair.count}
											</span>
										</Link>
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>
			) : null}

			{/* ── What the score is made of ───────────────────────────────────── */}
			<section className="bg-muted/30 border-y py-16">
				<div className="container mx-auto px-4">
					<div className="mx-auto max-w-5xl">
						<h2 className="mb-8 font-serif text-2xl tracking-tight">
							What we do that a star rating cannot
						</h2>

						<dl className="grid gap-8 sm:grid-cols-3">
							<div>
								<dt className="mb-2 font-medium">Read the text</dt>
								<dd className="text-muted-foreground text-sm leading-relaxed">
									We pull the full public review corpus and find the patterns
									that recur: missed appointments, billing surprises, work that
									had to be redone.
								</dd>
							</div>
							<div>
								<dt className="mb-2 font-medium">Cite the source</dt>
								<dd className="text-muted-foreground text-sm leading-relaxed">
									Every strength and caution we publish is backed by separate
									reviews you can expand and read on the listing itself.
								</dd>
							</div>
							<div>
								<dt className="mb-2 font-medium">Publish the rubric</dt>
								<dd className="text-muted-foreground text-sm leading-relaxed">
									The weights are public and the page that explains them renders
									from the same code that computes the scores.
								</dd>
							</div>
						</dl>

						<p className="text-muted-foreground mt-8 text-sm">
							No business can pay for a score, a rank, or the removal of an
							accurate caution.{' '}
							<Link
								className="text-foreground underline underline-offset-4"
								to="/scoring-method"
							>
								Read the method
							</Link>
						</p>
					</div>
				</div>
			</section>

			{/* ── Close ───────────────────────────────────────────────────────── */}
			<section className="border-t py-16">
				<div className="container mx-auto px-4">
					<div className="mx-auto flex max-w-5xl flex-wrap items-baseline justify-between gap-6">
						<p className="text-muted-foreground font-mono text-sm tabular-nums">
							{totalBusinesses.toLocaleString()} businesses tracked ·{' '}
							{categories.length}{' '}
							{categories.length === 1 ? 'category' : 'categories'} ·{' '}
							{cities.length} {cities.length === 1 ? 'city' : 'cities'}
							{rankedCount > 0 ? ` · ${rankedCount} ranked` : ''}
						</p>
						<div className="flex gap-3">
							<Button asChild variant="outline" size="sm">
								<Link to="/submit">Submit a business</Link>
							</Button>
							<Button asChild size="sm">
								<Link to="/categories">Start browsing</Link>
							</Button>
						</div>
					</div>
				</div>
			</section>
		</>
	)
}
